/**
 * The Billing tab's rail (§8.1, Board 11): **Cash timeline**, **Before you
 * send** (the billing rows) and **Defaults**, in that order. On an iPad and a
 * phone the same three cards are the bottom bar's Summary (§8.4).
 */
import {
  BILLING_KIND_CARDS, BILLING_TAB, invoiceTable, planWordsContext, type MarginScope, type QuoteCheck,
  type QuoteTree,
} from '@rowted/shared'
import { formatDollars } from '../../lib/format.js'
import { useState } from 'react'
import { Segmented, SectionLabel } from '../../components/primitives.js'
import { SCOPE_LABELS } from '../../builder/copy.js'
import { BeforeYouSendCard } from '../quote-rail.js'

/** The checks the Billing tab owns: its own scope, and the plan rows that were always billing's. */
const BILLING_KEYS = new Set(['plans', 'milestones', 'empty-plans'])
export function billingChecks(checks: QuoteCheck[]): QuoteCheck[] {
  return checks.filter((check) => check.scope === 'billing' || BILLING_KEYS.has(check.key))
}

/** One plan's invoices, by month, at one scope: the base plan or the plan with every option. */
function byMonth(quote: QuoteTree, all: boolean): Map<string, { label: string; amount: number }> {
  const months = new Map<string, { label: string; amount: number }>()
  for (const base of quote.plans) {
    const plan = all && base.allIn
      ? { ...base, total: base.allIn.total, invoices: base.allIn.invoices }
      : base
    const words = invoiceTable(plan, planWordsContext(plan, quote.visits)).rows
    plan.invoices.forEach((invoice, index) => {
      /*
       * A row with no date is money whose month nobody knows yet. It is
       * grouped under when it comes — on approval, or when the work is done —
       * rather than guessed into a month.
       */
      const undated = words[index]?.tone === 'approval' ? 'On approval' : 'When done'
      const key = invoice.on ? invoice.on.slice(0, 7) : `~${undated}`
      const label = invoice.on ? monthLabel(invoice.on) : undated
      const seen = months.get(key)
      months.set(key, { label, amount: (seen?.amount ?? 0) + invoice.amount })
    })
  }
  return months
}

/**
 * Every planned invoice, by the month it is issued; an undated one under the
 * event it waits for. **Base · Options · All** like the margin card (Aaron,
 * step 21 item 5.4): Base is what the plans bill if nothing is added, All is
 * with every option taken (`allIn`, planned on the server), and Options is
 * the difference — the money that only arrives if the customer says yes to
 * more.
 */
export function CashTimelineCard({ quote }: { quote: QuoteTree }) {
  const [scope, setScope] = useState<MarginScope>('base')
  const hasOptions = quote.plans.some((plan) => plan.allIn !== null)
  const base = byMonth(quote, false)
  const all = byMonth(quote, true)
  const months = scope === 'base' || !hasOptions
    ? base
    : scope === 'all'
      ? all
      : new Map([...all.entries()]
        .map(([key, row]) => [key, { ...row, amount: Math.round((row.amount - (base.get(key)?.amount ?? 0)) * 100) / 100 }] as const)
        .filter(([, row]) => row.amount !== 0))
  const rows = [...months.entries()].sort(([a], [b]) => a.localeCompare(b))
  const widest = Math.max(1, ...rows.map(([, row]) => row.amount))

  return (
    <Card>
      <SectionLabel className="block">{BILLING_TAB.cashTimeline}</SectionLabel>
      {hasOptions && (
        <div className="mt-2.5">
          <Segmented
            full
            label="Cash timeline scope"
            value={scope}
            onChange={setScope}
            options={(['base', 'options', 'all'] as const).map((value) => ({ value, label: SCOPE_LABELS[value] }))}
          />
        </div>
      )}
      {/*
        * One grid, so the label column is as wide as its widest label and every
        * bar still starts at the same x. A fixed 56px column clipped an undated
        * row to "When d…".
        */}
      <div className="mt-2.5 grid grid-cols-[minmax(56px,auto)_1fr_minmax(56px,auto)] items-center gap-x-2 gap-y-[7px]"
        data-cash-timeline="">
        {rows.map(([key, row]) => (
          <div key={key} className="contents">
            <span className="whitespace-nowrap text-[12px] text-text-muted">{row.label}</span>
            <span className="h-1.5 overflow-hidden rounded bg-line-faint">
              <span className="block h-full rounded bg-accent"
                style={{ width: `${Math.round((row.amount / widest) * 100)}%` }} />
            </span>
            <span className="tnum whitespace-nowrap text-right text-[12px] text-text">{formatDollars(row.amount)}</span>
          </div>
        ))}
      </div>
      <p className="m-0 mt-2.5 text-[11.5px] text-text-faint">
        {BILLING_TAB.cashCaption}<b className="font-semibold text-text-muted">{BILLING_TAB.cashIssued}</b>.
      </p>
    </Card>
  )
}

export function BillingChecksCard({
  checks, onGo,
}: { checks: QuoteCheck[]; onGo: (check: QuoteCheck) => void }) {
  /*
   * The billing rows, plus anything that blocks Send from elsewhere: "All
   * clear" beside a greyed Send was a card that was wrong about
   * the one thing it is for (audit B, cb-11). Go takes it to the Build tab.
   */
  const own = billingChecks(checks)
  const blocking = checks.filter((check) => check.level === 'blocking' && !own.includes(check))
  return <BeforeYouSendCard checks={[...own, ...blocking]} onGo={onGo} allClear />
}

/** The company's rules (§8.1), so a plan that looks odd can be checked against them. */
export function DefaultsCard({
  settings, onOpenSettings,
}: { settings: QuoteTree['billingDefaults']; onOpenSettings: () => void }) {
  const threshold = formatDollars(settings.depositThreshold)
  return (
    <Card>
      <SectionLabel className="block">{BILLING_TAB.defaults}</SectionLabel>
      <p className="m-0 mt-2 text-[13px] leading-[1.45] text-text-muted">
        Recurring visits → <b className="text-text">{BILLING_KIND_CARDS[settings.recurring].title}</b>.
        {' '}One-time under {threshold} → <b className="text-text">{BILLING_KIND_CARDS[settings.oneTime].title}</b>.
        {' '}One-time over {threshold} → <b className="text-text">Deposit {settings.defaultDepositPct}%, then the rest</b>.
        {' '}New plans start at <b className="text-text">the customer’s terms</b>. Set in{' '}
        <button type="button" onClick={onOpenSettings} className="text-accent hover:text-accent-dark">
          Company settings › Billing
        </button>
        ; the quoter can change any plan here.
      </p>
    </Card>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <section className="rounded-[14px] border border-line bg-surface px-[18px] py-4">{children}</section>
  )
}

/** "Sep", and the year too whenever it is not this one. */
function monthLabel(iso: string): string {
  const [year, month] = iso.split('-')
  const name = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(month) - 1] ?? iso
  return Number(year) === new Date().getFullYear() ? name : `${name} ${year!.slice(2)}`
}

/** The bottom bar's words on the Billing tab (§8.4): the season's total, the count, the first date. */
export function billingBar(quote: QuoteTree): { amount: number; sub: string } {
  const rows = quote.plans.flatMap((plan) => plan.invoices)
  const first = rows.map((row) => row.on).filter((on): on is string => on !== null).sort()[0] ?? null
  const label = first
    ? new Date(`${first}T00:00:00Z`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    : null
  return {
    amount: quote.plans.reduce((sum, plan) => sum + plan.total, 0),
    sub: BILLING_TAB.barSub(rows.length, label),
  }
}
